import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  User, Shield, Workflow, Plus, ChevronRight, Trash2, Save,
  GripVertical, Loader2, X, FileText, BookOpen,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const ProfileTab = () => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/profile`, { withCredentials: true }); setProfile(data && typeof data === 'object' ? data : null); } catch (e) {}
    })();
  }, []);

  if (!profile) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>;

  return (
    <div className="max-w-lg space-y-5" data-testid="settings-profile">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#6baed6] to-[#178a55] flex items-center justify-center text-white text-lg font-bold">
          {profile.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{profile.name}</p>
          <p className="text-xs text-foreground/40">{profile.email}</p>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#014d7d]/8 text-[#014d7d] capitalize">{profile.role}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-border/60 rounded-xl p-4">
          <p className="text-xl font-heading font-bold text-foreground">{profile.project_count}</p>
          <p className="text-[10px] text-foreground/40 font-semibold uppercase">Projects</p>
        </div>
        <div className="bg-white border border-border/60 rounded-xl p-4">
          <p className="text-xl font-heading font-bold text-foreground">{profile.session_count}</p>
          <p className="text-[10px] text-foreground/40 font-semibold uppercase">Sessions</p>
        </div>
      </div>
    </div>
  );
};

const WorkflowEditor = ({ workflow, onSave, onCancel }) => {
  const [name, setName] = useState(workflow?.name || "");
  const [desc, setDesc] = useState(workflow?.description || "");
  const [stages, setStages] = useState(workflow?.stages || []);

  const addStage = () => setStages(prev => [...prev, { name: "", description: "", artifacts: [] }]);
  const removeStage = (i) => setStages(prev => prev?.filter((_, idx) => idx !== i));
  const updateStage = (i, field, val) => setStages(prev => prev?.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  const addArtifact = (stageIdx) => setStages(prev => prev?.map((s, i) => i === stageIdx ? { ...s, artifacts: [...s.artifacts, { type: "", template: "", brief: "" }] } : s));
  const removeArtifact = (si, ai) => setStages(prev => prev?.map((s, i) => i === si ? { ...s, artifacts: s.artifacts?.filter((_, j) => j !== ai) } : s));
  const updateArtifact = (si, ai, field, val) => setStages(prev => prev?.map((s, i) => i === si ? { ...s, artifacts: s.artifacts?.map((a, j) => j === ai ? { ...a, [field]: val } : a) } : s));

  return (
    <div className="space-y-4" data-testid="workflow-editor">
      <div className="grid grid-cols-2 gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Workflow name..."
          className="h-10 px-3 text-sm border border-border/60 rounded-xl outline-none" data-testid="wf-name" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description..."
          className="h-10 px-3 text-sm border border-border/60 rounded-xl outline-none" data-testid="wf-desc" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider">Stages</p>
          <button onClick={addStage} className="text-[10px] font-semibold text-[#014d7d] hover:underline flex items-center gap-0.5" data-testid="add-stage-btn">
            <Plus className="w-3 h-3" /> Add Stage
          </button>
        </div>

        {stages?.map((stage, si) => (
          <div key={si} className="bg-white border border-border/60 rounded-xl p-4" data-testid={`stage-editor-${si}`}>
            <div className="flex items-center gap-2 mb-3">
              <GripVertical className="w-3.5 h-3.5 text-foreground/15" />
              <input value={stage.name} onChange={(e) => updateStage(si, "name", e.target.value)} placeholder="Stage name..."
                className="flex-1 h-8 px-2 text-xs font-semibold border border-border/40 rounded-lg outline-none" />
              <button onClick={() => removeStage(si)} className="text-foreground/45 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <textarea value={stage.description} onChange={(e) => updateStage(si, "description", e.target.value)} placeholder="Stage description..."
              className="w-full text-xs border border-border/40 rounded-lg px-2 py-1.5 outline-none resize-none mb-3" rows={2} />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-bold text-foreground/50 uppercase tracking-wider">Artifacts</p>
                <button onClick={() => addArtifact(si)} className="text-[9px] font-semibold text-[#014d7d] hover:underline flex items-center gap-0.5">
                  <Plus className="w-2.5 h-2.5" /> Add
                </button>
              </div>
              {stage.artifacts?.map((art, ai) => (
                <div key={ai} className="bg-[#f8f9fb] rounded-lg p-3 border border-border/30" data-testid={`artifact-editor-${si}-${ai}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-3 h-3 text-foreground/50" />
                    <input value={art.type} onChange={(e) => updateArtifact(si, ai, "type", e.target.value)} placeholder="Artifact type..."
                      className="flex-1 h-7 px-2 text-xs border border-border/30 rounded-md outline-none bg-white" />
                    <button onClick={() => removeArtifact(si, ai)} className="text-foreground/45 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                  <input value={art.template} onChange={(e) => updateArtifact(si, ai, "template", e.target.value)} placeholder="Template / format (e.g., MCQs, IMS CC 1.3)..."
                    className="w-full h-7 px-2 text-[11px] border border-border/30 rounded-md outline-none mb-1.5 bg-white" />
                  <textarea value={art.brief} onChange={(e) => updateArtifact(si, ai, "brief", e.target.value)} placeholder="Brief on recommended output..."
                    className="w-full text-[11px] border border-border/30 rounded-md px-2 py-1.5 outline-none resize-none bg-white" rows={2} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button onClick={() => onSave({ name, description: desc, stages })}
          className="text-xs font-semibold px-4 py-2 rounded-xl bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="save-workflow-btn">
          <Save className="w-3.5 h-3.5" /> Save Workflow
        </button>
        {onCancel && <button onClick={onCancel} className="text-xs font-semibold px-4 py-2 rounded-xl border border-border/60 text-foreground/50">Cancel</button>}
      </div>
    </div>
  );
};

const WorkflowsTab = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchWorkflows = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workflows`, { withCredentials: true });
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const handleSave = async (wfData) => {
    try {
      if (editing) {
        await axios.put(`${API}/workflows/${editing.id}`, wfData, { withCredentials: true });
      } else {
        await axios.post(`${API}/workflows`, wfData, { withCredentials: true });
      }
      setEditing(null);
      setCreating(false);
      fetchWorkflows();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/workflows/${id}`, { withCredentials: true });
      fetchWorkflows();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>;

  if (editing || creating) {
    return <WorkflowEditor workflow={editing} onSave={handleSave} onCancel={() => { setEditing(null); setCreating(false); }} />;
  }

  return (
    <div data-testid="settings-workflows">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-foreground/40">Define reusable pipelines with artifact templates for each stage.</p>
        <button onClick={() => setCreating(true)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="new-workflow-btn">
          <Plus className="w-3.5 h-3.5" /> New Workflow
        </button>
      </div>

      <div className="space-y-3">
        {workflows?.map(wf => (
          <div key={wf.id} className="bg-white border border-border/60 rounded-xl p-4" data-testid={`workflow-card-${wf.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="text-sm font-semibold text-foreground">{wf.name}</h4>
                <p className="text-xs text-foreground/40 mt-0.5">{wf.description}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => setEditing(wf)} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground" data-testid={`edit-wf-${wf.id}`}>Edit</button>
                <button onClick={() => handleDelete(wf.id)} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-red-100 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-wf-${wf.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
            {/* Stage flow */}
            <div className="flex items-center gap-1 mt-3">
              {(wf.stages || [])?.map((s, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="bg-[#f8f9fb] border border-border/40 rounded-lg px-2.5 py-1.5">
                    <p className="text-[10px] font-semibold text-foreground">{s.name}</p>
                    <p className="text-[9px] text-foreground/50">{(s.artifacts || []).length} artifacts</p>
                  </div>
                  {i < (wf.stages || []).length - 1 && <ChevronRight className="w-3 h-3 text-foreground/15 shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Settings = () => {
  const [tab, setTab] = useState("workflows");

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "workflows", label: "Workflows", icon: Workflow },
    { id: "team", label: "Team & Roles", icon: Shield },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="settings-page">
      <div className="px-6 pt-5 pb-0 bg-white border-b border-border/40">
        <h2 className="font-heading font-bold text-lg text-foreground mb-4">Settings</h2>
        <div className="flex gap-1">
          {tabs?.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 ${tab === t.id ? "border-[#014d7d] text-[#014d7d]" : "border-transparent text-foreground/40 hover:text-foreground/60"}`}
              data-testid={`settings-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {tab === "profile" && <ProfileTab />}
        {tab === "workflows" && <WorkflowsTab />}
        {tab === "team" && (
          <div className="text-center py-12 text-foreground/45">
            <Shield className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm font-semibold">Team & Roles</p>
            <p className="text-xs mt-1">Multi-tenant SSO / SCIM coming soon</p>
          </div>
        )}
      </div>
    </div>
  );
};
